import { type VFC, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router";

import { AttributesListWidget } from "../core/navigation-panel/AttributeWidget.js";
import GenericEntityGenomeView from "../genome-viewer/GenericEntityGenomeView.js";
import { type IGenomeViewOwnProps } from "../genome-viewer/GenomeView.js";
import { renderGeneAttributesWidgets } from "@/app-utils/attributes-widgets.tsx";
import { useLoadDispatchWithIdAsNumber } from "@/app-utils/hooks/useLoadDispatchWithIdAsNumber.ts";
import { getPaddedRange } from "@/app-utils/range.ts";
import { type IGeneState, shouldLoadGene } from "@/redux/modules/gene.ts";
import { getClassicGenomeBrowserTracks } from "@/redux/modules/genome-browser.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { TRACK_TYPE } from "@/settings/settings.js";

const GeneGenomeView: VFC = () => {
  const { geneId } = useParams<"geneId">();
  const { gene } = useSelector<IAppState, IGeneState>((state) => state.gene);
  const load = useLoadDispatchWithIdAsNumber(shouldLoadGene, geneId);

  useEffect(() => {
    void load();
  }, [load]);

  let genomeViewProps: IGenomeViewOwnProps;

  if (gene) {
    genomeViewProps = {
      genomeId: gene.genome.id,
      contigId: gene.region.contig.id,
      range: getPaddedRange(gene.region.range, gene.region.contig.length),
      genomes: [gene.genome],
      defaultTracks: getClassicGenomeBrowserTracks([TRACK_TYPE.SEQUENCE, TRACK_TYPE.GENE]),
      attributesWidgets: [
        <AttributesListWidget attributes={renderGeneAttributesWidgets(gene)} key={"gene-genome-view-widget-1"} />,
      ],
      title: gene?.name,
      subtitle: gene?.description || "Genome View",
    };
  }

  return <GenericEntityGenomeView genomeViewProps={genomeViewProps} loadEntityData={() => void load()} />;
};

export default GeneGenomeView;
